/** @jsx jsx */
import {
  BaseButton,
  Button,
  Grid,
  IconButton,
  Spacer,
  Spinner,
  Table,
  TextGroupMedium,
} from "@cd2/components/build/es6"
import { Form2 } from "@cd2/forms/build/es6"
import { P } from "@cd2/text/build/es6"
import { css, jsx } from "@emotion/core"
import React, { useState } from "react"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { buttonConfigs } from "../../constants/buttonConfigs"
import { icons } from "../../constants/icons"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { useSiteAlerts } from "../../hooks/useSiteAlerts"
import { IRecord } from "../../types/schema"
import { formatPrice } from "../../utils/utils"
import { calculateMarginFromPrice, formatMargin } from "./quoteOrderUtils"
import { useEvents } from "../../Components/EventsList"

type IProduct = IRecord<"products", "cost_price_pennies" | "stock_code">

type ICustomer = IRecord<"customers", "account_reference"> & {
  price_bands: (IRecord<"price_bands", "name" | "default_as_preferred" | "managing_users"> & {
    price_band_products: (IRecord<"price_band_products", "eos_price_pennies" | "price_pennies"> & {
      product: IRecord<"products", "stock_code" | "cost_price_pennies">
    })[]
  })[]
}

export function ManagePriceBandModal({
  productId,
  customerId,
  newPrice,
  onResolve,
}: {
  productId?: string
  customerId?: string
  onResolve: (state: boolean) => Promise<void>
  newPrice: number | null
}) {
  const events = useEvents()
  const alerts = useSiteAlerts()
  const { currentUser } = useGlobalState()

  function canEditPriceBand(prb: ICustomer["price_bands"][number]) {
    return (
      currentUser?.canEditCustomersPricing &&
      (currentUser.canUpdateManagingUsersOnPriceBands ||
        prb.managing_users.length === 0 ||
        prb.managing_users.some(user => user.id === currentUser?.id))
    )
  }
  const [customer, setCustomer] = useState<ICustomer | null>(null)
  const [product, setProduct] = useState<IProduct | null>(null)
  const [selectedPriceBand, setSelectedPriceBand] = useState<
    ICustomer["price_bands"][number] | null
  >(null)
  const skeem = window.skeem

  async function loadState() {
    if (customerId && productId) {
      const customer = await skeem.fetch2<ICustomer>({
        first: {
          name: "customers",
          attributes: [
            "account_reference",
            {
              name: "price_bands",
              attributes: [
                "name",
                "default_as_preferred",
                "managing_users",
                {
                  name: "price_band_products",
                  attributes: [
                    "price_pennies",
                    "eos_price_pennies",
                    { name: "product", attributes: ["cost_price_pennies", "stock_code"] },
                  ],
                  filter: {
                    matches: { product: productId, deleted: false },
                  },
                },
              ],
              filter: { matches: { locked: false, created_universal_price_band: null } },
            },
          ],
          matches: { id: customerId },
        },
      })
      const product = await skeem.fetch2<IProduct>({
        first: {
          name: "products",
          attributes: ["stock_code", "cost_price_pennies"],
          filter: { matches: { id: productId } },
        },
      })
      setProduct(product)
      setCustomer(customer)
      if (customer.price_bands.length === 1 && canEditPriceBand(customer.price_bands[0])) {
        setSelectedPriceBand(customer.price_bands[0])
      }
    } else {
      setCustomer(null)
      setProduct(null)
    }
  }

  useAsyncEffect(async () => {
    await loadState()
  }, [customerId, productId])

  async function handleSubmitNewPriceBand(data: { name: string }) {
    const existing = await skeem.fetch2<IRecord<"price_bands", "name">>({
      first: {
        name: "price_bands",
        attributes: ["name"],
        filter: {
          and: [
            { matches: { name: data.name.trim() } },
            {
              exists: { path: [{ name: "customers", filter: { matches: { id: customer?.id } } }] },
            },
          ],
        },
      },
    })
    if (existing) {
      alerts.genericError("A price band with this name already exists")
      return
    }
    const newBand = await skeem.mutate({
      price_bands: {
        create: {
          name: data.name,
          source_branch_id: currentUser.branch.id,
          manually_created: true,
          customers: { set: [customerId] },
        },
      },
    })
    events.associateWithInverse(
      "price_bands",
      newBand.id,
      { association: "customers", recordId: customerId!, nameAttr: "name" },
      "name",
    )
    await skeem.mutate({
      price_band_products: {
        create: {
          price_pennies: newPrice,
          eos_price_pennies: newPrice,
          price_band_name: data.name,
          source_branch_id: currentUser.branch.id,
          products_id: productId,
          product: { set: productId },
          price_bands: { set: newBand.id },
          price_bands_id: newBand.id,
        },
      },
    })
    await loadState()
    alerts.genericSuccess("Price band created")
  }

  async function updatePriceBand() {
    const priceBandProduct = selectedPriceBand?.price_band_products.find(
      p => p.product.id === productId,
    )
    if (!priceBandProduct) {
      return
    }
    await skeem.mutate({
      price_band_products: {
        update: { id: priceBandProduct.id, price_pennies: newPrice, eos_price_pennies: newPrice },
      },
    })
    alerts.genericSuccess("Prices updated")
    onResolve(true)
  }

  async function addToPriceBand() {
    if (!selectedPriceBand) {
      return
    }
    await skeem.mutate({
      price_band_products: {
        create: {
          price_pennies: newPrice,
          eos_price_pennies: newPrice,
          price_band_name: selectedPriceBand.name,
          source_branch_id: currentUser.branch.id,
          products_id: productId,
          product: { set: productId },
          price_bands: { set: selectedPriceBand.id },
          price_bands_id: selectedPriceBand.id,
          preferred_price: !!selectedPriceBand.default_as_preferred,
        },
      },
    })
    alerts.genericSuccess("Prices updated")
    onResolve(true)
  }

  if (!customerId || !productId) {
    return null
  }

  if (!customer || !product) {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
        `}
      >
        <Spinner size={48} color="black" />
      </div>
    )
  }

  // If they have 1 price band - click the button - pop up a modal saying change price on priceband from X => Y OR Add product to price band at X
  // If they have 2 price bands- select the price band then the above
  // If they have 0 price bands - ask them for the name of the price band and confirm they want it created - pre fill the box with the customers account reference

  const priceBands = customer.price_bands

  const isUpdate = !!selectedPriceBand && selectedPriceBand.price_band_products.length > 0
  const isAdd = !!selectedPriceBand && selectedPriceBand.price_band_products.length === 0
  const isCreate = priceBands.length === 0
  const isSelect = !selectedPriceBand && priceBands.length >= 1

  return (
    <div>
      <TextGroupMedium
        heading={`Manage price for ${product.stock_code}`}
        body={
          <React.Fragment>
            <Spacer />
            {isUpdate && (
              <P>
                Change price on the price band <b>{selectedPriceBand.name}</b>?
              </P>
            )}
            {isAdd && (
              <P>
                Add product <b>{product.stock_code}</b> to the price band{" "}
                <b>{selectedPriceBand.name}</b>?
              </P>
            )}
            {isCreate && (
              <P>
                This customer does not have a price band. Fill out the form below to create one.
              </P>
            )}
            {isSelect && <P>Select a customers price band to manage the price</P>}
            <Spacer />
          </React.Fragment>
        }
      />
      <Spacer />

      {/* Manage updating product on price band */}
      {/* A price band is selected, and the price band has the product we are checking against */}
      {isUpdate && (
        <Grid gap={8}>
          <Table
            data={[
              {
                price_pennies: selectedPriceBand.price_band_products[0].price_pennies ?? 0,
              },
            ]}
          >
            <Table.Column heading="Cost Price">
              {() =>
                formatPrice(product.cost_price_pennies ?? 0, { isPennies: true, currency: "£" })
              }
            </Table.Column>
            <Table.Column heading="Price Band Price/Margin">
              {row => {
                const marginValue = product.cost_price_pennies
                  ? calculateMarginFromPrice(product.cost_price_pennies, row.price_pennies)
                  : undefined

                return (
                  <div>
                    <P>{formatPrice(row.price_pennies, { isPennies: true, currency: "£" })}</P>
                    <P>{formatMargin(marginValue)}</P>
                  </div>
                )
              }}
            </Table.Column>
            <Table.Column heading="New Price/Margin">
              {() => {
                const marginValue = product.cost_price_pennies
                  ? calculateMarginFromPrice(product.cost_price_pennies, newPrice ?? 0)
                  : undefined

                return (
                  <div>
                    <P>{formatPrice(newPrice ?? 0, { isPennies: true, currency: "£" })}</P>
                    <P>{formatMargin(marginValue)}</P>
                  </div>
                )
              }}
            </Table.Column>
          </Table>
          <Spacer />
          <Grid columns={["max-content", "max-content"]} gap={8}>
            <Button leftIcon={icons.save} onClick={updatePriceBand}>
              Update Price
            </Button>
            <Button
              leftIcon={icons.cross}
              onClick={() => {
                if (isUpdate) {
                  setSelectedPriceBand(null)
                } else {
                  onResolve(false)
                }
              }}
              buttonColor="white"
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Manage adding product to price band */}
      {/* A price band is selected, but does not already have our product */}
      {isAdd && (
        <Grid gap={8}>
          <Table data={[{}]}>
            <Table.Column heading="Cost Price">
              {() =>
                formatPrice(product.cost_price_pennies ?? 0, { isPennies: true, currency: "£" })
              }
            </Table.Column>
            <Table.Column heading="New price">
              {() => (
                <div>
                  <P>{formatPrice(newPrice ?? 0, { isPennies: true, currency: "£" })}</P>
                </div>
              )}
            </Table.Column>
            <Table.Column heading="New margin">
              {() => {
                const marginValue = product.cost_price_pennies
                  ? calculateMarginFromPrice(product.cost_price_pennies, newPrice ?? 0)
                  : undefined

                return (
                  <div>
                    <P>{formatMargin(marginValue)}</P>
                  </div>
                )
              }}
            </Table.Column>
          </Table>
          <Spacer />
          <Grid columns={["max-content", "max-content"]} gap={8}>
            <Button leftIcon={icons.save} onClick={addToPriceBand}>
              Add Price
            </Button>
            <Button
              leftIcon={icons.cross}
              onClick={() => setSelectedPriceBand(null)}
              buttonColor="white"
            >
              Cancel
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Manage creating new price band */}
      {/* We have no price bands */}
      {isCreate && (
        <CreatePriceBandForm
          initialName={customer?.account_reference}
          onSubmit={handleSubmitNewPriceBand}
          onCancel={() => setSelectedPriceBand(null)}
        />
      )}

      {/* Manage select a price band */}
      {/* We have multiple price bands, but haven't selected one */}
      {isSelect && (
        <React.Fragment>
          <Table data={customer.price_bands}>
            <Table.Column
              css={css`
                width: 1px;
              `}
            >
              {row => (
                <IconButton
                  icon={icons.plus}
                  onClick={() => setSelectedPriceBand(row)}
                  disabled={!canEditPriceBand(row)}
                  square
                />
              )}
            </Table.Column>
            <Table.Column heading="Price Band">{row => row.name}</Table.Column>
            <Table.Column heading="Cost Price">
              {() =>
                formatPrice(product.cost_price_pennies ?? 0, { isPennies: true, currency: "£" })
              }
            </Table.Column>
            <Table.Column heading="Price Band Price/Margin">
              {row => {
                const oldPrice = row.price_band_products[0]?.price_pennies ?? 0
                const marginValue = product.cost_price_pennies
                  ? calculateMarginFromPrice(product.cost_price_pennies, oldPrice)
                  : undefined

                return (
                  <div>
                    <P>{formatPrice(oldPrice, { isPennies: true, currency: "£" })}</P>
                    <P>{formatMargin(marginValue)}</P>
                  </div>
                )
              }}
            </Table.Column>

            <Table.Column heading="New Price/Margin">
              {() => {
                const marginValue = product.cost_price_pennies
                  ? calculateMarginFromPrice(product.cost_price_pennies, newPrice ?? 0)
                  : undefined

                return (
                  <div>
                    <P>{formatPrice(newPrice ?? 0, { isPennies: true, currency: "£" })}</P>
                    <P>{formatMargin(marginValue)}</P>
                  </div>
                )
              }}
            </Table.Column>
          </Table>
          <Spacer />
          <div>
            <Button leftIcon={icons.cross} onClick={() => onResolve(false)} buttonColor="white">
              Cancel
            </Button>
          </div>
        </React.Fragment>
      )}
      <Spacer />
    </div>
  )
}

function CreatePriceBandForm({
  onSubmit,
  initialName,
  onCancel,
}: {
  onSubmit(data: { name: string }): void
  initialName: string
  onCancel: () => void
}) {
  return (
    <Form2.Form onSubmit={onSubmit} initialData={{ name: initialName }}>
      <Form2.Text field="name" label="Name" validations={{ presence: true }} />
      <Grid columns={["max-content", "max-content"]} gap={8}>
        <BaseButton {...buttonConfigs.formSaveButton} />
        <Button leftIcon={icons.cross} onClick={onCancel} buttonColor="white">
          Cancel
        </Button>
      </Grid>
    </Form2.Form>
  )
}
